import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import {
  PlusIcon,
  UserPlusIcon,
  UserGroupIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../../constants/colors'
import { TESTIDS } from '../../../../../config/testids'

export interface OrgMemberOption {
  userId: string
  name: string
  email: string
}

export interface AddSprintParticipantProps {
  onAddParticipant: (participant: { name: string; email: string }) => void
  /** Members of the facilitator's active organization — the primary way to add participants. */
  orgMembers?: OrgMemberOption[]
  /** Emails already added to the sprint, so they are filtered out of the org dropdown. */
  existingEmails?: string[]
  className?: string
}

const labelStyle: React.CSSProperties = {
  color: INTERAX_COLORS.thunderGrey,
  fontFamily: INTERAX_TYPOGRAPHY.body3.fontFamily,
  fontWeight: INTERAX_TYPOGRAPHY.body3.fontWeight,
  fontSize: INTERAX_TYPOGRAPHY.body3.fontSize,
  lineHeight: INTERAX_TYPOGRAPHY.body3.lineHeight
}

const fieldStyle: React.CSSProperties = {
  backgroundColor: INTERAX_COLORS.offBlack,
  color: INTERAX_COLORS.white,
  borderColor: INTERAX_COLORS.thunderGrey
}

export const AddSprintParticipant: React.FC<AddSprintParticipantProps> = ({
  onAddParticipant,
  orgMembers,
  existingEmails = [],
  className = ''
}) => {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [selectedUserId, setSelectedUserId] = useState('')
  const [memberQuery, setMemberQuery] = useState('')
  const [isMemberListOpen, setIsMemberListOpen] = useState(false)
  const memberDropdownRef = useRef<HTMLDivElement>(null)

  const hasOrg = Boolean(orgMembers && orgMembers.length > 0)
  // Start in org mode whenever the facilitator has organization members to pick from.
  const [mode, setMode] = useState<'org' | 'manual'>(hasOrg ? 'org' : 'manual')

  const existingEmailSet = useMemo(() => new Set(existingEmails.map((e) => e.trim().toLowerCase())), [existingEmails])

  const availableMembers = useMemo(
    () => (orgMembers ?? []).filter((m) => m.email && !existingEmailSet.has(m.email.trim().toLowerCase())),
    [orgMembers, existingEmailSet]
  )

  const selectedMember = useMemo(
    () => availableMembers.find((m) => m.userId === selectedUserId),
    [availableMembers, selectedUserId]
  )

  const filteredMembers = useMemo(() => {
    const q = memberQuery.trim().toLowerCase()
    if (!q) return availableMembers
    return availableMembers.filter((m) => m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q))
  }, [availableMembers, memberQuery])

  // Close the member dropdown when clicking outside of it.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (memberDropdownRef.current && !memberDropdownRef.current.contains(event.target as Node)) {
        setIsMemberListOpen(false)
      }
    }
    if (isMemberListOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isMemberListOpen])

  const handleSelectMember = (userId: string) => {
    setSelectedUserId(userId)
    setIsMemberListOpen(false)
    setMemberQuery('')
  }

  const resetFields = () => {
    setName('')
    setEmail('')
    setSelectedUserId('')
    setMemberQuery('')
    setIsMemberListOpen(false)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (mode === 'org') {
      const member = availableMembers.find((m) => m.userId === selectedUserId)
      if (!member) return
      onAddParticipant({ name: member.name.trim() || member.email, email: member.email.trim() })
      resetFields()
      return
    }
    if (name.trim() && email.trim()) {
      onAddParticipant({ name: name.trim(), email: email.trim() })
      resetFields()
    }
  }

  const canSubmit = mode === 'org' ? Boolean(selectedUserId) : Boolean(name.trim() && email.trim())

  const toggleButtonStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: 'none',
    border: `1px solid ${INTERAX_COLORS.sun40}`,
    borderRadius: 8,
    padding: '8px 12px',
    cursor: 'pointer',
    color: INTERAX_COLORS.sun,
    fontFamily: INTERAX_TYPOGRAPHY.body3.fontFamily,
    fontSize: INTERAX_TYPOGRAPHY.body3.fontSize,
    fontWeight: 700
  }

  return (
    <div
      className={`p-6 rounded-lg border ${className}`}
      style={{ backgroundColor: INTERAX_COLORS.heritageBlue40, borderColor: INTERAX_COLORS.heritageBlue }}
    >
      <h3
        className="font-semibold mb-4"
        style={{
          color: INTERAX_COLORS.white,
          fontFamily: INTERAX_TYPOGRAPHY.headline4.fontFamily,
          fontWeight: INTERAX_TYPOGRAPHY.headline4.fontWeight,
          fontSize: INTERAX_TYPOGRAPHY.headline4.fontSize,
          lineHeight: INTERAX_TYPOGRAPHY.headline4.lineHeight
        }}
      >
        {hasOrg
          ? t('facilitator.trainingSprint.addParticipants.addFromOrganization')
          : t('facilitator.trainingSprint.addParticipants.addSprintParticipants')}
      </h3>

      <form onSubmit={handleSubmit} className="space-y-4">
        {hasOrg && mode === 'org' && (
          <>
            <div ref={memberDropdownRef} style={{ position: 'relative' }}>
              <label className="block text-sm font-medium mb-2" style={labelStyle}>
                {t('facilitator.trainingSprint.addParticipants.selectMember')}
              </label>

              {/* Search-as-you-type trigger */}
              <button
                type="button"
                onClick={() => availableMembers.length > 0 && setIsMemberListOpen((prev) => !prev)}
                disabled={availableMembers.length === 0}
                aria-haspopup="listbox"
                aria-expanded={isMemberListOpen}
                aria-label={t('facilitator.trainingSprint.addParticipants.selectMember')}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border-2 focus:outline-none focus:ring-2"
                style={{
                  ...fieldStyle,
                  borderColor: isMemberListOpen ? INTERAX_COLORS.sun : INTERAX_COLORS.thunderGrey,
                  cursor: availableMembers.length === 0 ? 'not-allowed' : 'pointer',
                  opacity: availableMembers.length === 0 ? 0.6 : 1
                }}
              >
                <span
                  style={{
                    flex: 1,
                    textAlign: 'left',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: selectedMember ? INTERAX_COLORS.white : INTERAX_COLORS.thunderGrey
                  }}
                >
                  {selectedMember
                    ? selectedMember.name
                      ? `${selectedMember.name} — ${selectedMember.email}`
                      : selectedMember.email
                    : availableMembers.length > 0
                      ? t('facilitator.trainingSprint.addParticipants.selectMember')
                      : t('facilitator.trainingSprint.addParticipants.noOrgMembersAvailable')}
                </span>
                <ChevronDownIcon
                  style={{
                    width: 18,
                    height: 18,
                    color: INTERAX_COLORS.thunderGrey,
                    flexShrink: 0,
                    transform: isMemberListOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }}
                />
              </button>

              {/* Dropdown panel with search input + filtered members */}
              {isMemberListOpen && (
                <div
                  role="listbox"
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    right: 0,
                    zIndex: 1200,
                    backgroundColor: INTERAX_COLORS.offBlack,
                    border: `1px solid ${INTERAX_COLORS.thunderGrey}`,
                    borderRadius: 8,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                    overflow: 'hidden'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      margin: 8,
                      padding: '6px 8px',
                      borderRadius: 6,
                      backgroundColor: INTERAX_COLORS.heritageBlue
                    }}
                  >
                    <MagnifyingGlassIcon
                      style={{ width: 16, height: 16, color: INTERAX_COLORS.thunderGrey, flexShrink: 0 }}
                    />
                    <input
                      type="search"
                      value={memberQuery}
                      onChange={(e) => setMemberQuery(e.target.value)}
                      placeholder={t('facilitator.trainingSprint.addParticipants.searchMembers')}
                      autoFocus
                      style={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        color: INTERAX_COLORS.white
                      }}
                    />
                    {memberQuery && (
                      <button
                        type="button"
                        onClick={() => setMemberQuery('')}
                        aria-label={t('common.clear', 'Clear')}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <XMarkIcon style={{ width: 14, height: 14, color: INTERAX_COLORS.thunderGrey }} />
                      </button>
                    )}
                  </div>

                  <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                    {filteredMembers.length === 0 ? (
                      <div style={{ ...labelStyle, padding: '12px 16px' }}>
                        {t('facilitator.trainingSprint.addParticipants.noOrgMembersAvailable')}
                      </div>
                    ) : (
                      filteredMembers.map((m) => (
                        <button
                          key={m.userId}
                          type="button"
                          role="option"
                          aria-selected={m.userId === selectedUserId}
                          onClick={() => handleSelectMember(m.userId)}
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            border: 'none',
                            padding: '8px 16px',
                            cursor: 'pointer',
                            color: INTERAX_COLORS.white,
                            backgroundColor: m.userId === selectedUserId ? INTERAX_COLORS.heritageBlue40 : 'transparent'
                          }}
                        >
                          {m.name ? `${m.name} — ${m.email}` : m.email}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3" aria-hidden>
              <span style={{ flex: 1, height: 1, backgroundColor: INTERAX_COLORS.white10 }} />
              <span style={{ ...labelStyle, textTransform: 'uppercase' }}>
                {t('facilitator.trainingSprint.addParticipants.orDivider')}
              </span>
              <span style={{ flex: 1, height: 1, backgroundColor: INTERAX_COLORS.white10 }} />
            </div>

            <button
              type="button"
              style={toggleButtonStyle}
              onClick={() => setMode('manual')}
              data-testid={TESTIDS.SPRINT_ADD_NEW_PARTICIPANT_BTN}
            >
              <UserPlusIcon style={{ width: 16, height: 16 }} />
              {t('facilitator.trainingSprint.addParticipants.addNewInstead')}
            </button>
          </>
        )}

        {mode === 'manual' && (
          <>
            <div>
              <label className="block text-sm font-medium mb-2" style={labelStyle}>
                {t('facilitator.trainingSprint.addParticipants.participantName')}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid={TESTIDS.SPRINT_PARTICIPANT_NAME_INPUT}
                placeholder={t('facilitator.trainingSprint.addParticipants.enterName')}
                className="w-full px-3 py-2 rounded-lg border-2 focus:outline-none focus:ring-2"
                style={fieldStyle}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={labelStyle}>
                {t('facilitator.trainingSprint.addParticipants.emailAddress')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={TESTIDS.SPRINT_PARTICIPANT_EMAIL_INPUT}
                placeholder="gamer@example.com"
                className="w-full px-3 py-2 rounded-lg border-2 focus:outline-none focus:ring-2"
                style={fieldStyle}
              />
            </div>

            {hasOrg && (
              <button type="button" style={toggleButtonStyle} onClick={() => setMode('org')}>
                <UserGroupIcon style={{ width: 16, height: 16 }} />
                {t('facilitator.trainingSprint.addParticipants.selectFromOrgInstead')}
              </button>
            )}
          </>
        )}

        <button
          type="submit"
          disabled={!canSubmit}
          data-testid={TESTIDS.SPRINT_ADD_PARTICIPANT_BTN}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold transition-colors"
          style={{
            backgroundColor: canSubmit ? INTERAX_COLORS.sun : INTERAX_COLORS.thunderGrey20,
            color: canSubmit ? INTERAX_COLORS.offBlack : INTERAX_COLORS.white70,
            cursor: canSubmit ? 'pointer' : 'not-allowed',
            fontFamily: INTERAX_TYPOGRAPHY.body1.fontFamily,
            fontWeight: INTERAX_TYPOGRAPHY.body1.fontWeight,
            fontSize: INTERAX_TYPOGRAPHY.body1.fontSize,
            lineHeight: INTERAX_TYPOGRAPHY.body1.lineHeight
          }}
        >
          <PlusIcon className="w-5 h-5" />
          {t('facilitator.trainingSprint.addParticipants.addParticipant')}
        </button>
      </form>
    </div>
  )
}
